import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/material';
import { Button, Modal, Table, Text } from '@mantine/core';
import Spinner from '../../components/Spinner/Spinner';
import base from '../../apis';
import { formatNumber } from '../../utils/functions';
import { toast } from 'react-toastify';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '20px',
  },
}));

const headers = ['Agent', 'Broker Name', 'Broker Address', 'Broker EIN', 'Amount ($)', 'Mark as Paid'];

function ReportComps() {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [elements, setElements] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const [isAll, setIsAll] = useState(false);

  const getComps = useCallback(() => {
    setElements([]);
    base
      .get(`/compensations/agent-report/`)
      .then(({ data }) => {
        if (data.length === 0) {
          toast.warn('No pending requests');
        }
        const newElements = data.map((element) => ({
          id: element.id,
          agent: `${element.agent.user.first_name} ${element.agent.user.last_name}`,
          broker_name: element.agent.brokerage_name,
          broker_address: element.agent.brokerage_address,
          broker_ein: element.agent.brokers_ein,
          amount: element.amount,
        }));
        setElements(newElements);
      })
      .catch((err) => {
        toast.error('Could not get data');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const [selectedAgent, setSelectedAgent] = useState(null);
  const chooseForPayment = (agent, isAll) => {
    setModalOpen(true);
    setSelectedAgent(agent);
    setIsAll(false);
  };

  const markSingleAsPaid = () => {
    setIsLoading(true);
    let call = isAll
      ? base.post('/compensations/redraw-all-agent-money/')
      : base.post(`/compensations/redraw-agent-money/`, {
          request_id: selectedAgent.id,
        });
    call
      .then(({ data }) => {
        toast.success('Marked as paid');
        setElements(isAll ? [] : elements.filter((element) => element.id !== selectedAgent.id));
        setModalOpen(false);
      })
      .catch((err) => {
        toast.error('Could not mark as paid');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const markAllAsPaid = () => {
    setIsAll(true);
    setModalOpen(true);
  };

  useEffect(() => {
    getComps();
  }, [getComps]);

  return (
    <>
      {isLoading && <Spinner />}
      <Modal opened={modalOpen} title="Payment Confirmation" onClose={() => setModalOpen(false)}>
        Are you sure you want to mark as paid?
        <Box display="flex" justifyContent="flex-end" gap="10px" ml="auto" mt={2}>
          <Button variant="outline" color="red" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="outline" color="var(--primary-color)" onClick={markSingleAsPaid}>
            Confirm
          </Button>
        </Box>
      </Modal>
      <Box className={classes.root}>
        <Box display="flex" justifyContent="space-between">
          <Text weight={700}>Compensation Reports</Text>
          <Box display="flex" gap="10px">
            <Button variant="outline" color="red" onClick={markAllAsPaid}>
              Mark All as Paid
            </Button>
          </Box>
        </Box>

        <Table mt="sm">
          <thead style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}>
            <tr style={{ color: 'inherit' }}>
              {headers.map((header) => (
                <th key={header} style={{ color: 'inherit' }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {elements.map((element) => (
              <tr key={element.id} style={{ cursor: 'pointer' }}>
                <td>{element.agent}</td>
                <td>{element.broker_name}</td>
                <td>{element.broker_address}</td>
                <td>{element.broker_ein}</td>
                <td>{formatNumber(element.amount, 2)}</td>
                <td>
                  <Button variant="outline" onClick={() => chooseForPayment(element)}>
                    Mark as Paid
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Box>
    </>
  );
}

export default ReportComps;
